// Full-history modal for an entity's activity feed (property/customer/etc).
//
// Replaces the inline `Tải thêm` pager on the property-detail page with a
// centered modal (Dialog on desktop, bottom Sheet on mobile) that exposes:
//
//   - Desktop (>= 1024px):
//       - Left sidebar: 3 filter groups — category, time range, actor.
//         Counts are derived from the loaded set (page-1 grows the set when
//         the consumer hits "Tải thêm").
//       - Right column: search box, sort toggle, time-bucketed timeline.
//         Filters/search/sort run client-side over the accumulated rows.
//       - Header: eyebrow + title + N-activities chip + CSV export + close.
//       - Footer: visible/total counter + close button.
//   - Mobile (< 1024px): full-screen single-column Sheet.
//       - Top bar inside the sheet: back arrow + BDS code (mono) + close X.
//       - Header block: eyebrow + big title + count chip + subtitle line.
//       - Search input (with magnifying-glass prefix).
//       - Horizontal scrollable filter-pill row (category only — actor and
//         time range are intentionally dropped to save vertical space).
//       - Time-bucketed groups using the same ActivityRow as desktop.
//       - Load-more button.
//       - Sticky footer: visible/total counter + "Đi đến ngày" stub.
//
// Data flow: we call `useActivities` directly with `size=100` and let the
// component own pagination (no shared state with the inline 4-row timeline
// that renders behind the modal — they each call useActivities, React
// Query dedupes the request when keys overlap).
//
// CSV export is library-free: we render rows into RFC 4180-style CSV via
// a tiny helper and trigger a `<a download>` click on a Blob URL.

import { useMemo, useState } from "react";
import { ArrowLeft, CalendarDays, Download, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ActivityRow } from "@/features/activity/activity-row";
import {
  useActivities,
  type ActivityLogResponse,
} from "@/features/activity/api";
import {
  ACTIVITY_CATEGORY_DOT_COLORS,
  ACTIVITY_CATEGORY_LABELS,
  ACTIVITY_CATEGORY_ORDER,
  categorize,
  type ActivityCategoryKey,
} from "@/features/activity/categorize";
import {
  BUCKET_LABELS,
  BUCKET_ORDER,
  bucketOf,
  type BucketKey,
} from "@/features/activity/time-bucket";
import { formatActivityDetail } from "@/features/activity/format-activity-detail";
import { labelForAction } from "@/features/dashboard/activity-action-labels";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface ActivityHistoryDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  entityType: string;
  entityId: number;
  /** e.g. "BDS-0012" — shown in the eyebrow + CSV filename. */
  headerLabel: string;
  /** e.g. building name — shown in the modal title. */
  headerTitle: string;
}

// ---------------------------------------------------------------------------
// Time-range filter (loaded-set relative)
// ---------------------------------------------------------------------------

type TimeRangeKey = "ALL" | "LAST_7" | "LAST_30";

const TIME_RANGE_LABELS: Record<TimeRangeKey, string> = {
  ALL: "Tất cả",
  LAST_7: "7 ngày qua",
  LAST_30: "30 ngày qua",
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function inRange(createdAt: string, key: TimeRangeKey, now: number): boolean {
  if (key === "ALL") return true;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return false;
  const cutoff = key === "LAST_7" ? now - 7 * MS_PER_DAY : now - 30 * MS_PER_DAY;
  return t >= cutoff;
}

// ---------------------------------------------------------------------------
// Top-level dialog
// ---------------------------------------------------------------------------

export default function ActivityHistoryDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  headerLabel,
  headerTitle,
}: ActivityHistoryDialogProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="flex h-[min(820px,90vh)] max-h-[90vh] w-[min(1200px,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
        >
          <DialogTitle className="sr-only">
            Lịch sử thay đổi — {headerTitle}
          </DialogTitle>
          {open ? (
            <DialogBody
              entityType={entityType}
              entityId={entityId}
              headerLabel={headerLabel}
              headerTitle={headerTitle}
              onClose={() => onOpenChange(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="flex h-[100vh] max-h-[100vh] flex-col gap-0 overflow-hidden p-0"
      >
        <SheetTitle className="sr-only">
          Lịch sử thay đổi — {headerTitle}
        </SheetTitle>
        {open ? (
          <MobileBody
            entityType={entityType}
            entityId={entityId}
            headerLabel={headerLabel}
            headerTitle={headerTitle}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Body — owns data + filter state; layout switches to a single column on
// narrow viewports (the sidebar collapses into a top strip).
// ---------------------------------------------------------------------------

interface DialogBodyProps {
  entityType: string;
  entityId: number;
  headerLabel: string;
  headerTitle: string;
  onClose: () => void;
}

function DialogBody({
  entityType,
  entityId,
  headerLabel,
  headerTitle,
  onClose,
}: DialogBodyProps) {
  // Pagination: load page 0 at size=100 up-front; consumer clicks "Tải thêm"
  // to grow the accumulated set if the backend has more pages.
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<ActivityLogResponse[]>([]);
  const [activeCategory, setActiveCategory] =
    useState<ActivityCategoryKey>("ALL");
  const [timeRange, setTimeRange] = useState<TimeRangeKey>("ALL");
  const [actorIds, setActorIds] = useState<ReadonlySet<number>>(new Set());
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<"NEWEST" | "OLDEST">("NEWEST");

  const query = useActivities({
    entityType,
    entityId,
    page,
    size: 100,
  });

  // Merge each page into the accumulated set. Dedupe by id so React Query
  // refetches don't double-render. Done outside an effect because the data
  // is referentially stable per page key (matches the inline timeline's
  // pattern).
  if (query.data !== undefined) {
    const incoming = query.data.items;
    const known = new Set(accumulated.map((a) => a.id));
    const additions = incoming.filter((a) => !known.has(a.id));
    if (additions.length > 0) {
      setAccumulated((prev) => [...prev, ...additions]);
    }
  }

  const totalItems = query.data?.totalItems ?? accumulated.length;
  const totalPages = query.data?.totalPages ?? 0;
  const hasMore = page + 1 < totalPages;

  // ---------------- derived: counts for sidebar buttons ----------------
  // `now` is sampled once at first render — the sidebar's 7/30-day
  // window counts shouldn't drift while the user is filtering. The body
  // unmounts when the modal closes, so reopening re-samples.
  // useState lazy-init is the React-Compiler-safe way to call an impure
  // function during initial render (matches MobileBody below).
  const [now] = useState(() => Date.now());

  const categoryCounts = useMemo(() => {
    const counts = new Map<ActivityCategoryKey, number>();
    counts.set("ALL", accumulated.length);
    for (const item of accumulated) {
      const key = categorize(item.action ?? "");
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [accumulated]);

  const timeRangeCounts = useMemo(() => {
    const out: Record<TimeRangeKey, number> = { ALL: 0, LAST_7: 0, LAST_30: 0 };
    for (const item of accumulated) {
      out.ALL += 1;
      const at = item.createdAt ?? "";
      if (inRange(at, "LAST_7", now)) out.LAST_7 += 1;
      if (inRange(at, "LAST_30", now)) out.LAST_30 += 1;
    }
    return out;
  }, [accumulated, now]);

  const actors = useMemo(() => {
    const map = new Map<number, { name: string; count: number }>();
    for (const item of accumulated) {
      const id = item.actorUserId ?? 0;
      const name = item.actorFullName ?? "Hệ thống";
      const prev = map.get(id);
      if (prev) {
        prev.count += 1;
      } else {
        map.set(id, { name, count: 1 });
      }
    }
    return Array.from(map.entries())
      .map(([id, v]) => ({ id, name: v.name, count: v.count }))
      .sort((a, b) => b.count - a.count);
  }, [accumulated]);

  // ---------------- derived: filtered + searched + sorted rows --------
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = accumulated.filter((item) => {
      const cat = categorize(item.action ?? "");
      if (activeCategory !== "ALL" && cat !== activeCategory) return false;
      if (!inRange(item.createdAt ?? "", timeRange, now)) return false;
      if (actorIds.size > 0) {
        const aid = item.actorUserId ?? 0;
        if (!actorIds.has(aid)) return false;
      }
      if (term.length > 0) {
        const haystack = `${item.actorFullName ?? ""} ${labelForAction(
          item.action ?? "",
        )} ${item.detail ?? ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
    filtered.sort((a, b) => {
      const ta = Date.parse(a.createdAt ?? "") || 0;
      const tb = Date.parse(b.createdAt ?? "") || 0;
      return sortOrder === "NEWEST" ? tb - ta : ta - tb;
    });
    return filtered;
  }, [accumulated, activeCategory, timeRange, actorIds, search, sortOrder, now]);

  // Group the visible set by bucket key, preserving the bucket order.
  const grouped = useMemo(() => {
    const map = new Map<BucketKey, ActivityLogResponse[]>();
    for (const item of visible) {
      const key = bucketOf(item.createdAt ?? "", now);
      const arr = map.get(key);
      if (arr) {
        arr.push(item);
      } else {
        map.set(key, [item]);
      }
    }
    // For OLDEST sort we still want buckets newest→oldest at the top
    // grouping level — flipping the order would put OLDER at the top
    // which is visually wrong. The within-bucket sort already reflects
    // the user's choice.
    return BUCKET_ORDER.map((k) => ({ key: k, items: map.get(k) ?? [] })).filter(
      (b) => b.items.length > 0,
    );
  }, [visible, now]);

  // ---------------- CSV export -----------------------------------------
  const handleExport = () => {
    const filename = csvFilename(headerLabel);
    const csv = rowsToCsv(visible);
    downloadCsv(filename, csv);
  };

  const toggleActor = (id: number) => {
    setActorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <Header
        headerLabel={headerLabel}
        headerTitle={headerTitle}
        totalItems={totalItems}
        onExport={handleExport}
        onClose={onClose}
        disableExport={visible.length === 0}
      />

      {/* Body grid */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:grid lg:grid-cols-[260px_1fr]">
        {/* Sidebar */}
        <aside className="border-b border-border bg-muted/30 p-4 lg:max-h-full lg:overflow-y-auto lg:border-r lg:border-b-0">
          <Sidebar
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            categoryCounts={categoryCounts}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            timeRangeCounts={timeRangeCounts}
            actors={actors}
            activeActorIds={actorIds}
            onToggleActor={toggleActor}
          />
        </aside>

        {/* Timeline column */}
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Search + sort toolbar */}
          <div className="flex shrink-0 items-center gap-2 border-b border-border bg-card px-4 py-3">
            <div className="relative flex-1">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm trong lịch sử (giá, tên, ghi chú…)"
                aria-label="Tìm trong lịch sử"
                className="pr-10 pl-8"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
              >
                ⌘F
              </span>
            </div>
            <SortToggle value={sortOrder} onChange={setSortOrder} />
          </div>

          {/* Timeline body */}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <TimelineBody
              isPending={query.isPending && accumulated.length === 0}
              isError={query.isError}
              onRetry={() => void query.refetch()}
              grouped={grouped}
              visibleCount={visible.length}
              accumulatedCount={accumulated.length}
              hasMore={hasMore}
              isFetching={query.isFetching}
              onLoadMore={() => setPage((p) => p + 1)}
              totalItems={totalItems}
            />
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer
        visibleCount={visible.length}
        totalItems={totalItems}
        onClose={onClose}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header / Footer
// ---------------------------------------------------------------------------

interface HeaderProps {
  headerLabel: string;
  headerTitle: string;
  totalItems: number;
  onExport: () => void;
  onClose: () => void;
  disableExport: boolean;
}

function Header({
  headerLabel,
  headerTitle,
  totalItems,
  onExport,
  onClose,
  disableExport,
}: HeaderProps) {
  return (
    <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border bg-card px-5 py-4">
      <div className="min-w-0">
        <div className="font-mono text-[11px] tracking-[0.18em] text-muted-foreground uppercase">
          LỊCH SỬ THAY ĐỔI · {headerLabel}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-2">
          <h2 className="text-[16px] leading-tight font-bold tracking-tight">
            {headerTitle}
          </h2>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
            {totalItems} hoạt động
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onExport}
          disabled={disableExport}
        >
          <Download aria-hidden="true" className="size-3.5" />
          Xuất CSV
        </Button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

function Footer({
  visibleCount,
  totalItems,
  onClose,
}: {
  visibleCount: number;
  totalItems: number;
  onClose: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between border-t border-border bg-card px-5 py-3 text-[12px]">
      <span className="text-muted-foreground">
        Hiện <b className="text-foreground">{visibleCount}</b> / {totalItems}{" "}
        hoạt động
      </span>
      <Button type="button" variant="outline" size="sm" onClick={onClose}>
        Đóng
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

interface SidebarProps {
  activeCategory: ActivityCategoryKey;
  onCategoryChange: (next: ActivityCategoryKey) => void;
  categoryCounts: Map<ActivityCategoryKey, number>;
  timeRange: TimeRangeKey;
  onTimeRangeChange: (next: TimeRangeKey) => void;
  timeRangeCounts: Record<TimeRangeKey, number>;
  actors: ReadonlyArray<{ id: number; name: string; count: number }>;
  activeActorIds: ReadonlySet<number>;
  onToggleActor: (id: number) => void;
}

function Sidebar({
  activeCategory,
  onCategoryChange,
  categoryCounts,
  timeRange,
  onTimeRangeChange,
  timeRangeCounts,
  actors,
  activeActorIds,
  onToggleActor,
}: SidebarProps) {
  return (
    <div className="flex flex-col gap-5">
      <CategoryGroup
        activeCategory={activeCategory}
        onChange={onCategoryChange}
        counts={categoryCounts}
      />
      <TimeRangeGroup
        active={timeRange}
        onChange={onTimeRangeChange}
        counts={timeRangeCounts}
      />
      <ActorGroup
        actors={actors}
        active={activeActorIds}
        onToggle={onToggleActor}
      />
    </div>
  );
}

function CategoryGroup({
  activeCategory,
  onChange,
  counts,
}: {
  activeCategory: ActivityCategoryKey;
  onChange: (next: ActivityCategoryKey) => void;
  counts: Map<ActivityCategoryKey, number>;
}) {
  const renderItem = (key: ActivityCategoryKey) => {
    const count = counts.get(key) ?? 0;
    if (key !== "ALL" && count === 0) return null;
    const isActive = key === activeCategory;
    return (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={isActive}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left text-[12px] transition-colors",
          isActive
            ? "border-indigo-300 bg-indigo-50 text-indigo-700"
            : "border-transparent bg-card text-foreground hover:bg-muted",
        )}
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <span
            aria-hidden="true"
            className={cn(
              "size-2 shrink-0 rounded-full",
              ACTIVITY_CATEGORY_DOT_COLORS[key],
            )}
          />
          <span className="truncate">{ACTIVITY_CATEGORY_LABELS[key]}</span>
        </span>
        <span
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px]",
            isActive
              ? "bg-indigo-100 text-indigo-700"
              : "bg-muted text-muted-foreground",
          )}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-1.5">
      <GroupHeader>LOẠI HOẠT ĐỘNG</GroupHeader>
      {renderItem("ALL")}
      {ACTIVITY_CATEGORY_ORDER.map(renderItem)}
    </div>
  );
}

function TimeRangeGroup({
  active,
  onChange,
  counts,
}: {
  active: TimeRangeKey;
  onChange: (next: TimeRangeKey) => void;
  counts: Record<TimeRangeKey, number>;
}) {
  const items: TimeRangeKey[] = ["LAST_7", "LAST_30", "ALL"];
  return (
    <div className="flex flex-col gap-1.5">
      <GroupHeader>KHOẢNG THỜI GIAN</GroupHeader>
      {items.map((k) => {
        const isActive = k === active;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            aria-pressed={isActive}
            className={cn(
              "flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left text-[12px] transition-colors",
              isActive
                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                : "border-transparent bg-card text-foreground hover:bg-muted",
            )}
          >
            <span>{TIME_RANGE_LABELS[k]}</span>
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px]",
                isActive
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {counts[k]}
            </span>
          </button>
        );
      })}
      {/* Custom range — stub for now per brief. */}
      <button
        type="button"
        disabled
        aria-disabled="true"
        title="Sắp ra mắt"
        className="flex w-full cursor-not-allowed items-center justify-between rounded-md border border-dashed border-border px-2.5 py-1.5 text-left text-[12px] text-muted-foreground opacity-70"
      >
        <span>Tuỳ chỉnh…</span>
        <span className="text-[11px]">(sắp ra mắt)</span>
      </button>
    </div>
  );
}

function ActorGroup({
  actors,
  active,
  onToggle,
}: {
  actors: ReadonlyArray<{ id: number; name: string; count: number }>;
  active: ReadonlySet<number>;
  onToggle: (id: number) => void;
}) {
  if (actors.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <GroupHeader>NGƯỜI THAY ĐỔI</GroupHeader>
      {actors.map((a) => {
        const isActive = active.has(a.id);
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => onToggle(a.id)}
            aria-pressed={isActive}
            className={cn(
              "flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-[12px] transition-colors",
              isActive
                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                : "border-transparent bg-card text-foreground hover:bg-muted",
            )}
          >
            <span
              aria-hidden="true"
              className="flex size-6 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-semibold text-foreground"
            >
              {initialsFor(a.name)}
            </span>
            <span className="min-w-0 flex-1 truncate">{a.name}</span>
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px]",
                isActive
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {a.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function GroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[11px] tracking-[0.18em] text-muted-foreground uppercase">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sort toggle
// ---------------------------------------------------------------------------

function SortToggle({
  value,
  onChange,
}: {
  value: "NEWEST" | "OLDEST";
  onChange: (next: "NEWEST" | "OLDEST") => void;
}) {
  return (
    <div
      role="group"
      aria-label="Thứ tự sắp xếp"
      className="inline-flex shrink-0 overflow-hidden rounded-md border border-border bg-card text-[12px]"
    >
      <SortBtn active={value === "NEWEST"} onClick={() => onChange("NEWEST")}>
        Mới nhất
      </SortBtn>
      <SortBtn active={value === "OLDEST"} onClick={() => onChange("OLDEST")}>
        Cũ nhất
      </SortBtn>
    </div>
  );
}

function SortBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "px-2.5 py-1.5 transition-colors",
        active ? "bg-foreground text-background" : "text-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Timeline body — bucketed groups, load-more, empty/error states
// ---------------------------------------------------------------------------

interface TimelineBodyProps {
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  grouped: ReadonlyArray<{ key: BucketKey; items: ActivityLogResponse[] }>;
  visibleCount: number;
  accumulatedCount: number;
  hasMore: boolean;
  isFetching: boolean;
  onLoadMore: () => void;
  totalItems: number;
}

function TimelineBody({
  isPending,
  isError,
  onRetry,
  grouped,
  visibleCount,
  accumulatedCount,
  hasMore,
  isFetching,
  onLoadMore,
  totalItems,
}: TimelineBodyProps) {
  if (isPending) {
    return (
      <div
        aria-busy="true"
        aria-label="Đang tải lịch sử"
        className="flex flex-col gap-3"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-2 pt-1">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        <p>Không thể tải lịch sử.</p>
        <Button variant="link" size="sm" onClick={onRetry} className="mt-1">
          Thử lại
        </Button>
      </div>
    );
  }

  if (visibleCount === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        Không có hoạt động phù hợp với bộ lọc.
      </div>
    );
  }

  const remaining = Math.max(0, totalItems - accumulatedCount);

  return (
    <div className="flex flex-col gap-4">
      {grouped.map((group) => (
        <div key={group.key} className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2">
            <h3 className="text-[12px] font-semibold text-foreground">
              {BUCKET_LABELS[group.key]}
            </h3>
            <span className="font-mono text-[11px] text-muted-foreground">
              · {group.items.length} sự kiện
            </span>
          </div>
          <ol className="flex flex-col">
            {group.items.map((item, idx) => (
              <ActivityRow
                key={item.id}
                item={item}
                isLast={idx === group.items.length - 1}
              />
            ))}
          </ol>
        </div>
      ))}
      {hasMore ? (
        <div className="flex justify-center pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetching}
          >
            {isFetching
              ? "Đang tải..."
              : remaining > 0
                ? `Tải thêm (${remaining} còn lại)`
                : "Tải thêm"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers (CSV, initials)
// ---------------------------------------------------------------------------

function initialsFor(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === undefined) return "?";
  if (parts.length === 1) return (parts[0]?.[0] ?? "?").toUpperCase();
  const first = parts[0][0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return `${first}${last}`.toUpperCase();
}

function csvFilename(internalCode: string): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  // Trim non-alphanumeric (e.g. dashes are fine, but spaces aren't).
  const safe = internalCode.replace(/[^A-Za-z0-9-_]/g, "") || "history";
  return `${safe}-history-${yyyy}${mm}${dd}.csv`;
}

function csvEscape(value: string): string {
  // RFC 4180: double-quote any field containing comma / quote / newline.
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function rowsToCsv(items: ReadonlyArray<ActivityLogResponse>): string {
  const lines: string[] = ["time,actor,action,detail"];
  for (const item of items) {
    const action = item.action ?? "";
    const detail = formatActivityDetail(action, item.detail ?? null).summary ?? "";
    lines.push(
      [
        csvEscape(item.createdAt ?? ""),
        csvEscape(item.actorFullName ?? "Hệ thống"),
        csvEscape(labelForAction(action)),
        csvEscape(detail),
      ].join(","),
    );
  }
  return lines.join("\r\n");
}

function downloadCsv(filename: string, content: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  // Prepend the UTF-8 BOM so Excel on Windows recognises Vietnamese
  // characters when the file is double-clicked.
  const blob = new Blob(["﻿" + content], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Release the blob URL on the next tick so Safari has time to start
  // the download before the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ---------------------------------------------------------------------------
// Mobile body — single-column full-screen layout. Tracks
// /Users/datnguyen/Downloads/activity history mobile.html. Reuses the same
// data flow as DialogBody (page/accumulated/categorize/bucket) but drops
// the time-range and actor filters per the design.
// ---------------------------------------------------------------------------

function MobileBody({
  entityType,
  entityId,
  headerLabel,
  headerTitle,
  onClose,
}: DialogBodyProps) {
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<ActivityLogResponse[]>([]);
  const [activeCategory, setActiveCategory] =
    useState<ActivityCategoryKey>("ALL");
  const [search, setSearch] = useState("");

  const query = useActivities({
    entityType,
    entityId,
    page,
    size: 100,
  });

  if (query.data !== undefined) {
    const incoming = query.data.items;
    const known = new Set(accumulated.map((a) => a.id));
    const additions = incoming.filter((a) => !known.has(a.id));
    if (additions.length > 0) {
      setAccumulated((prev) => [...prev, ...additions]);
    }
  }

  const totalItems = query.data?.totalItems ?? accumulated.length;
  const totalPages = query.data?.totalPages ?? 0;
  const hasMore = page + 1 < totalPages;

  // `now` is captured once per mount via useState so React's purity rule
  // (no impure reads during render) is satisfied. The activity list is
  // short-lived enough that a stable "now" reference is fine — buckets
  // and date labels would only drift if the modal sat open across a
  // calendar day boundary, which is not a realistic UX path.
  const [now] = useState(() => Date.now());

  const categoryCounts = useMemo(() => {
    const counts = new Map<ActivityCategoryKey, number>();
    counts.set("ALL", accumulated.length);
    for (const item of accumulated) {
      const key = categorize(item.action ?? "");
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [accumulated]);

  // Number of distinct actors in the loaded set — used in the subtitle line
  // "{N} người đóng góp".
  const uniqueActorCount = useMemo(() => {
    const set = new Set<string>();
    for (const item of accumulated) {
      // Group system actions under a single "system" bucket.
      const key =
        item.actorUserId === null || item.actorUserId === undefined
          ? "system"
          : String(item.actorUserId);
      set.add(key);
    }
    return set.size;
  }, [accumulated]);

  // Oldest entry date (DD/MM) — drives the "Hoạt động từ {date}" subtitle.
  const oldestDateLabel = useMemo(() => {
    if (accumulated.length === 0) return null;
    let oldest = Number.POSITIVE_INFINITY;
    for (const item of accumulated) {
      const t = Date.parse(item.createdAt ?? "");
      if (Number.isFinite(t) && t < oldest) oldest = t;
    }
    if (!Number.isFinite(oldest)) return null;
    const d = new Date(oldest);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
  }, [accumulated]);

  // Filtered + sorted (newest first) rows. The mobile branch keeps the
  // sort fixed to newest — the toggle was a desktop-only affordance.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = accumulated.filter((item) => {
      const cat = categorize(item.action ?? "");
      if (activeCategory !== "ALL" && cat !== activeCategory) return false;
      if (term.length > 0) {
        const haystack = `${item.actorFullName ?? ""} ${labelForAction(
          item.action ?? "",
        )} ${item.detail ?? ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
    filtered.sort((a, b) => {
      const ta = Date.parse(a.createdAt ?? "") || 0;
      const tb = Date.parse(b.createdAt ?? "") || 0;
      return tb - ta;
    });
    return filtered;
  }, [accumulated, activeCategory, search]);

  const grouped = useMemo(() => {
    const map = new Map<BucketKey, ActivityLogResponse[]>();
    for (const item of visible) {
      const key = bucketOf(item.createdAt ?? "", now);
      const arr = map.get(key);
      if (arr) {
        arr.push(item);
      } else {
        map.set(key, [item]);
      }
    }
    return BUCKET_ORDER.map((k) => ({ key: k, items: map.get(k) ?? [] })).filter(
      (b) => b.items.length > 0,
    );
  }, [visible, now]);

  // Category pills — Tất cả first, then every non-empty category in
  // ACTIVITY_CATEGORY_ORDER. Each pill carries a colored dot prefix when
  // the category has one (ALL gets no dot in the design).
  const pillKeys: ActivityCategoryKey[] = useMemo(() => {
    const out: ActivityCategoryKey[] = ["ALL"];
    for (const k of ACTIVITY_CATEGORY_ORDER) {
      if ((categoryCounts.get(k) ?? 0) > 0) out.push(k);
    }
    return out;
  }, [categoryCounts]);

  const remaining = Math.max(0, totalItems - accumulated.length);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Top bar — back arrow + BDS code (mono, centered) + close X */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-card px-3 py-2.5">
        <button
          type="button"
          onClick={onClose}
          aria-label="Quay lại"
          className="flex size-8 items-center justify-center rounded-lg text-foreground hover:bg-muted"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
        </button>
        <span className="font-mono text-[12px] tracking-wider text-foreground">
          {headerLabel}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="flex size-8 items-center justify-center rounded-lg text-foreground hover:bg-muted"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Header block */}
        <div className="px-[18px] pt-4 pb-3">
          <div className="font-mono text-[11px] tracking-[0.18em] text-ink-faint uppercase">
            LỊCH SỬ THAY ĐỔI
          </div>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-2">
            <h1 className="text-[28px] leading-tight font-bold tracking-tight">
              {headerTitle}
            </h1>
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-foreground">
              {totalItems}
            </span>
          </div>
          <p className="mt-1 text-[12px] text-muted-foreground">
            {oldestDateLabel !== null
              ? `Hoạt động từ ${oldestDateLabel} đến nay · ${uniqueActorCount} người đóng góp`
              : "Chưa có hoạt động"}
          </p>
        </div>

        {/* Search */}
        <div className="px-[18px] pb-3">
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm trong lịch sử…"
              aria-label="Tìm trong lịch sử"
              className="h-10 rounded-xl pl-9"
            />
          </div>
        </div>

        {/* Category pills — horizontal scrollable */}
        <div className="no-scrollbar overflow-x-auto px-[18px] pb-3">
          <div className="flex w-max gap-1.5">
            {pillKeys.map((k) => {
              const isActive = k === activeCategory;
              const count = categoryCounts.get(k) ?? 0;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setActiveCategory(k)}
                  aria-pressed={isActive}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap transition-colors",
                    isActive
                      ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                      : "border-border bg-card text-foreground",
                  )}
                >
                  {k !== "ALL" ? (
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-1.5 shrink-0 rounded-full",
                        ACTIVITY_CATEGORY_DOT_COLORS[k],
                      )}
                    />
                  ) : null}
                  <span>{ACTIVITY_CATEGORY_LABELS[k]}</span>
                  <span
                    className={cn(
                      "rounded-full px-1 font-mono text-[11px]",
                      isActive
                        ? "bg-indigo-100 text-indigo-700"
                        : "text-muted-foreground",
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Time-bucketed groups */}
        <div className="px-[18px] pb-4">
          <MobileTimelineBody
            isPending={query.isPending && accumulated.length === 0}
            isError={query.isError}
            onRetry={() => void query.refetch()}
            grouped={grouped}
            visibleCount={visible.length}
            hasMore={hasMore}
            isFetching={query.isFetching}
            onLoadMore={() => setPage((p) => p + 1)}
            remaining={remaining}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-card px-[18px] py-3 pb-[22px]">
        <span className="text-[12px] text-muted-foreground">
          <b className="text-foreground">{visible.length}</b> / {totalItems}{" "}
          hoạt động
        </span>
        <button
          type="button"
          disabled
          aria-disabled="true"
          title="Sắp ra mắt"
          className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-dashed border-border px-2.5 py-1.5 text-[12px] font-semibold text-muted-foreground opacity-70"
        >
          <CalendarDays aria-hidden="true" className="size-3.5" />
          Đi đến ngày
        </button>
      </div>
    </div>
  );
}

interface MobileTimelineBodyProps {
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  grouped: ReadonlyArray<{ key: BucketKey; items: ActivityLogResponse[] }>;
  visibleCount: number;
  hasMore: boolean;
  isFetching: boolean;
  onLoadMore: () => void;
  remaining: number;
}

function MobileTimelineBody({
  isPending,
  isError,
  onRetry,
  grouped,
  visibleCount,
  hasMore,
  isFetching,
  onLoadMore,
  remaining,
}: MobileTimelineBodyProps) {
  if (isPending) {
    return (
      <div
        aria-busy="true"
        aria-label="Đang tải lịch sử"
        className="flex flex-col gap-3"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-2 pt-1">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        <p>Không thể tải lịch sử.</p>
        <Button variant="link" size="sm" onClick={onRetry} className="mt-1">
          Thử lại
        </Button>
      </div>
    );
  }

  if (visibleCount === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        Không có hoạt động phù hợp với bộ lọc.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {grouped.map((group) => (
        <div key={group.key} className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-[12px] font-semibold text-foreground">
              {BUCKET_LABELS[group.key]}
            </h3>
            <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
              {group.items.length}
            </span>
          </div>
          <ol className="flex flex-col">
            {group.items.map((item, idx) => (
              <ActivityRow
                key={item.id}
                item={item}
                isLast={idx === group.items.length - 1}
              />
            ))}
          </ol>
        </div>
      ))}
      {hasMore ? (
        <div className="flex justify-center pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetching}
            className="w-full rounded-xl"
          >
            {isFetching
              ? "Đang tải..."
              : remaining > 0
                ? `Tải thêm (${remaining} còn lại)`
                : "Tải thêm"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

